import dayjs from "dayjs";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { api, endpoint } from "@/api";
import CanvasjsBarChart from "@/components/Charts/CanvasjsBarChart";
import CanvasjsBarLineComboChart from "@/components/Charts/CanvasjsBarLineComboChart";
import CanvasjsPieChart from "@/components/Charts/CanvasPieChart";
import ChartJsBarChart from "@/components/Charts/ChartJsBarChart";
import ChartJsLineChart from "@/components/Charts/ChartJsLineChart";
import SplineChart from "@/components/Charts/SplineChart";
import LoadingOverlay from "@/components/Shared/LoadingOverlay";
import { flare } from "@flareapp/js";

const ReportChartFetch = ({ data, type, mainDashboard, isDark: isDarkProps, isSelected }) => {
  const theme = useSelector((state) => state.theme.isDark);
  const isDark = isDarkProps === undefined ? theme : isDarkProps;
  const ref = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [lineGraphData, setLineGraphData] = useState([]);
  const [barGraphData, setBarGraphData] = useState([]);
  const { isFullScreen } = useSelector((state) => state.theme);

  const {
    startDate,
    endDate,
    key,
    quickStartDate,
    quickEndDate,
    xAxis: filterAxis,
    peakHours,
    // comparison filters
    interval: rawInterval,
    window,
    operator: comparisonOperator,
    quantity,
    comparisonPeriods,
  } = useSelector((state) => state.dashboard?.filters || {});

  const {
    chartTitle,
    locations,
    comboLocations,
    xAxis,
    operator,
    timeRange,
    customXAxis,
    customOperator,
    isTitleHidden,
    interval: formInterval,
    showThumbsUp,
    chartType,
    intervalTime,
    intervalValue,
  } = data;

  const interval = rawInterval || (intervalTime || 1) * (intervalValue || 1);

  const pullData = async ({ startDate, endDate, locations }, comparisonPayload) => {
    try {
      let params = {
        locations,
        xAxis: xAxis || "day",
        operator,
      };

      params.interval = formInterval || interval || undefined;

      if (xAxis === "custom" && timeRange?.length) {
        params = {
          ...params,
          customRangeStart: moment(timeRange[0], "YYYY-MM-DD HH:mm").format("YYYY/MM/DD : HH:mm"),
          customRangeEnd: moment(timeRange[1], "YYYY-MM-DD HH:mm").format("YYYY/MM/DD : HH:mm"),
        };
      }
      if (mainDashboard) {
        if (comparisonPayload?.comparisonInterval) {
          params = {
            ...params,
            peakHours,
            ...comparisonPayload,
          };
        } else {
          params = {
            ...params,
            peakHours,
            xAxis: filterAxis || xAxis,
            startDate,
            endDate,
            customXAxis,
            customOperator,
          };
        }
      }

      // console.log({ mainDashboard, params });

      const data = await api.get(endpoint.get_chart_data, {
        params,
      });
      if (data?.success) {
        return data?.results;
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // get location data
  const getChartData = async (locations, type) => {
    try {
      const validComparisonPeriods = (comparisonPeriods || []).filter((item) => item?.date);
      let gData = [];
      if (
        isSelected &&
        interval &&
        window &&
        comparisonOperator &&
        quantity &&
        validComparisonPeriods?.length &&
        ["bar", "line", "comboBar"].includes(type)
      ) {
        let data = [];
        validComparisonPeriods.map((item) => {
          let startDate = dayjs(item.date).format("YYYY/MM/DD");
          let endDate = dayjs(item.date).add(Number(quantity), window).format("YYYY/MM/DD");
          data.push({ startDate, endDate, customOperator: comparisonOperator, comparisonInterval: interval });
        });
        let gridData = await Promise.all(
          data.map((item) => pullData({ locations }, item)),
          locations,
        );
        const firstStartDate = data?.[0]?.startDate.split("T")?.[0];

        data.map((item, idx) => {
          let temp = gridData[idx];
          const crntStartDate = item.startDate.split("T")?.[0];
          gridData[idx] = temp.map((tempItem) => {
            tempItem.name = `${tempItem.name} ${dayjs(item.startDate).format("DD/MM/YYYY")} - ${dayjs(item.endDate).format("DD/MM/YYYY")}`;
            tempItem._id = `${tempItem._id} ${dayjs(item.startDate).format("DD/MM/YYYY")} - ${dayjs(item.endDate).format("DD/MM/YYYY")}`;
            tempItem.chart = tempItem.chart?.map((chartItem) => {
              const dayDiff = dayjs(chartItem.x.split("T")?.[0]).diff(crntStartDate, "day");
              chartItem.x = `${dayjs(firstStartDate).add(dayDiff, "day").format("YYYY-MM-DD")}T${chartItem.x.split("T")?.[1]}`;
              return chartItem;
            });
            return tempItem;
          });
        });
        // let finalData = [];
        gridData.map((item) => {
          gData = gData.concat(item?.length ? item : []);
        });
        // setGraphData(finalData);
      } else if (quickStartDate && quickEndDate) {
        gData = await pullData({ startDate: quickStartDate, endDate: quickEndDate, locations });
        // setGraphData(data || []);
      } else {
        gData = await pullData({ startDate, endDate, locations });
        // setGraphData(data || []);
      }
      gData = Array.isArray(gData) ? gData.sort((a, b) => a.name.localeCompare(b.name)) : [];
      if (type === "line") {
        setLineGraphData(gData);
      } else if (type === "bar") {
        setBarGraphData(gData);
      } else {
        setGraphData(gData);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      if (type === "comboBar") {
        await getChartData(JSON.stringify(comboLocations?.line), "line");
        await getChartData(JSON.stringify(comboLocations?.bar), "bar");
      } else {
        await getChartData(JSON.stringify(locations));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [
    JSON.stringify({
      data,
      locations: data.locations,
      startDate,
      endDate,
      quickStartDate,
      quickEndDate,
      key,
      interval,
      window,
      comparisonOperator,
      quantity,
      comparisonPeriods,
      isSelected,
      peakHours,
    }),
  ]);

  useEffect(() => {
    if (isFullScreen) {
      setIsLoading(true);

      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  }, [isFullScreen]);
  // useEffect(()=>{},[showThumbsUp, colo])

  const chartProps = {
    data: type === "comboBar" ? { line: lineGraphData, bar: barGraphData } : graphData,
    height: ref.current?.clientHeight,
    isDark,
    // key: locations,
    ...data,
  };

  // console.log({ chartProps });

  return (
    <div ref={ref} className="h-100">
      <LoadingOverlay isLoading={isLoading} className="h-100">
        <div className="ql">
          <div className="ql-editor p-0 pb-1" style={{ textAlign: "center" }}>
            {!isTitleHidden && <div dangerouslySetInnerHTML={{ __html: chartTitle }}></div>}
          </div>
        </div>

        {(graphData?.length > 0 || lineGraphData?.length > 0 || barGraphData?.length > 0) && !isLoading ? (
          type === "line" ? (
            chartType === "chartJs" ? (
              <ChartJsLineChart {...chartProps} showThumbsUp={showThumbsUp} />
            ) : (
              <SplineChart {...chartProps} showThumbsUp={showThumbsUp} />
            )
          ) : type === "bar" ? (
            chartType === "chartJs" ? (
              <ChartJsBarChart {...chartProps} showThumbsUp={showThumbsUp} />
            ) : (
              <CanvasjsBarChart {...chartProps} showThumbsUp={showThumbsUp} />
            )
          ) : type === "pie" ? (
            <CanvasjsPieChart {...chartProps} />
          ) : type === "comboBar" ? (
            <CanvasjsBarLineComboChart {...chartProps} showThumbsUp={showThumbsUp} />
          ) : (
            ""
            // <NivoChartHome data={graphData} type={type} xAxis={xAxis} colorSchema={colorSchema} />
          )
        ) : isLoading ? (
          <h2
            style={{
              textAlign: "center",
              fontSize: "15px",
              color: "green",
              marginTop: "50px",
            }}
          >
            Loading
          </h2>
        ) : (
          <h2 className="delayed-message">No data found!</h2>
        )}
      </LoadingOverlay>
    </div>
  );
};

export default ReportChartFetch;

ReportChartFetch.propTypes = {
  data: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  isDark: PropTypes.bool,
  mainDashboard: PropTypes.bool,
  isSelected: PropTypes.bool,
};
